import { ServiceEndpoints } from "../../constants/ServiceEndpoints";
import {
  BusinessSectionErrorEnvelope,
  BusinessSectionNode,
  CreateBusinessSectionPayload,
  RawBusinessSection,
  RenameOrMoveBusinessSectionPayload,
  ReorderBusinessSectionsPayload,
} from "../../interfaces/BusinessSectionObject";
import {
  convertRawBusinessSection,
  convertRawBusinessSectionTree,
} from "../converters/backendToUi/businessSectionConvertor";
import {
  buildCreateSectionPayload,
  buildRenameOrMoveSectionPayload,
  buildReorderSectionsPayload,
} from "../converters/uiToBackend/BusinessSectionConvertor";
import { fetchOrThrowFriendly } from "../helpers/ApiErrorHelper";
import { getRequest, postRequest, putRequest, deleteRequest } from "../http/httpRequest";

/**
 * Business-section endpoints ALWAYS return HTTP 200, even on failure — never
 * branch on `response.ok` for these (see ServiceEndpoints.BUSINESS_SECTIONS_BASE's
 * doc comment). Check `processingStatus.status` instead; `errors[0]` explains
 * a FAILED status. Error codes are not unique per reason (see
 * BusinessSectionErrorCodes' doc comment), so a caller that needs to
 * distinguish same-code failures reads `error.message` (the description), not
 * just `error.code`.
 */
function throwOnSectionFailure(data: unknown, fallback: string): void {
  if (!data || typeof data !== "object") return;
  const envelope = data as BusinessSectionErrorEnvelope;
  if (envelope.processingStatus?.status !== "FAILED") return;
  const first = envelope.errors?.[0];
  throw sectionError(
    first?.description || envelope.processingStatus?.message || fallback,
    first?.code
  );
}

/** Errors carry `code` through Redux Toolkit's miniSerializeError so
 *  `.unwrap()` callers can branch on it (still only as coarse as the code
 *  itself is — see the doc comment on BusinessSectionErrorCodes). */
function sectionError(message: string, code?: string): Error {
  const err = new Error(message);
  if (code) (err as Error & { code?: string }).code = code;
  return err;
}

/** `contents` is a single-element array on create/rename success. */
function coerceSectionSingle(data: unknown): RawBusinessSection | null {
  const contents = (data as { contents?: unknown[] } | null)?.contents;
  const first = Array.isArray(contents) ? contents[0] : null;
  return first && typeof first === "object" ? (first as RawBusinessSection) : null;
}

/**
 * GET .../business/{businessId} — public, no auth. Full nested section tree
 * for the business, sorted by displayOrder then name server-side.
 */
async function getSectionTree(businessId: string): Promise<BusinessSectionNode[]> {
  const endpoint =
    `${ServiceEndpoints.BUSINESS_SECTIONS_BASE}/business/` +
    encodeURIComponent(businessId);
  const response = await fetchOrThrowFriendly(() => getRequest(endpoint, false));
  const data = await response.json().catch(() => null);
  throwOnSectionFailure(data, "Couldn't load sections for this business");
  const contents = (data as { contents?: RawBusinessSection[] } | null)?.contents;
  return convertRawBusinessSectionTree(contents);
}

async function createSection(
  params: Parameters<typeof buildCreateSectionPayload>[0]
): Promise<BusinessSectionNode> {
  const body: CreateBusinessSectionPayload = buildCreateSectionPayload(params);
  const response = await fetchOrThrowFriendly(() =>
    postRequest(ServiceEndpoints.BUSINESS_SECTIONS_BASE, body, true)
  );
  const data = await response.json().catch(() => null);
  throwOnSectionFailure(data, "Couldn't create this section");
  const created = coerceSectionSingle(data);
  if (!created) throw sectionError("Couldn't create this section.");
  return convertRawBusinessSection(created);
}

/**
 * Full replace — `params` must carry the section's CURRENT name/parentSectionId
 * for whichever field isn't actually changing (see
 * RenameOrMoveBusinessSectionPayload's doc comment). Rejects moving a section
 * under itself or one of its own descendants (CYCLE_DETECTED, code "400").
 */
async function renameOrMoveSection(
  sectionId: string,
  params: Parameters<typeof buildRenameOrMoveSectionPayload>[0]
): Promise<BusinessSectionNode> {
  const endpoint = `${ServiceEndpoints.BUSINESS_SECTIONS_BASE}/${encodeURIComponent(sectionId)}`;
  const body: RenameOrMoveBusinessSectionPayload = buildRenameOrMoveSectionPayload(params);
  const includeBearerToken = true;
  const response = await fetchOrThrowFriendly(() => putRequest(endpoint, body, includeBearerToken));
  const data = await response.json().catch(() => null);
  throwOnSectionFailure(data, "Couldn't update this section");
  const updated = coerceSectionSingle(data);
  if (!updated) throw sectionError("Couldn't update this section.");
  return convertRawBusinessSection(updated);
}

/**
 * `cascade=true` deletes the section and all nested subsections; any product
 * assigned to any of them has its section CLEARED (not deleted — the product
 * itself is untouched). Default (`cascade=false`) fails with HAS_CHILDREN or
 * HAS_PRODUCTS (both code "409") if the section isn't empty — read
 * `error.message` for which one, and surface it as "move or reassign these
 * first" rather than a generic error.
 */
async function deleteSection(
  sectionId: string,
  cascade = false
): Promise<BusinessSectionNode | null> {
  const endpoint =
    `${ServiceEndpoints.BUSINESS_SECTIONS_BASE}/${encodeURIComponent(sectionId)}` +
    `?cascade=${cascade}`;
  const includeBearerToken = true;
  const response = await fetchOrThrowFriendly(() => deleteRequest(endpoint, includeBearerToken));
  const data = await response.json().catch(() => null);
  throwOnSectionFailure(data, "Couldn't delete this section");
  const deleted = coerceSectionSingle(data);
  return deleted ? convertRawBusinessSection(deleted) : null;
}

/**
 * Bulk-persists a sibling drag-reorder in one call, instead of N sequential
 * renameOrMoveSection calls (each of which would also need to correctly
 * resend the sibling's current name/parentSectionId, being a full replace).
 * All-or-nothing: if any entry in `orders` isn't a direct child of
 * `parentSectionId`, the whole request fails (NOT_SIBLING) and nothing
 * changes. Returns EVERY sibling under that parent (touched and untouched),
 * already sorted — replace local state with this directly rather than
 * re-sorting client-side.
 */
async function reorderSections(
  params: Parameters<typeof buildReorderSectionsPayload>[0]
): Promise<BusinessSectionNode[]> {
  const endpoint = `${ServiceEndpoints.BUSINESS_SECTIONS_BASE}/reorder`;
  const body: ReorderBusinessSectionsPayload = buildReorderSectionsPayload(params);
  const includeBearerToken = true;
  const response = await fetchOrThrowFriendly(() => putRequest(endpoint, body, includeBearerToken));
  const data = await response.json().catch(() => null);
  throwOnSectionFailure(data, "Couldn't save the new order");
  const contents = (data as { contents?: RawBusinessSection[] } | null)?.contents;
  return convertRawBusinessSectionTree(contents);
}

const BusinessSectionApiService = {
  getSectionTree,
  createSection,
  renameOrMoveSection,
  deleteSection,
  reorderSections,
};

export default BusinessSectionApiService;
